import {
  type Building,
  PoiCategories,
  type PoiCategory,
  type Pois,
} from "@/libs/models";

const NO_POI_CATEGORY = 148;

/*
 ** Filters POI categories to return only those that have a POI in the building.
 */
export function filterCategoriesWithExistingPOIs(
  poiCategories: PoiCategories | null,
  pois: Pois,
  keepTopLevels: boolean = true,
): PoiCategories | null {
  if (!poiCategories) return null;
  return poiCategories.filterByIds(
    [...new Set(pois?.toArray().map((p) => p.categoryId))],
    keepTopLevels,
  );
}

/*
 * Filters POI categories and subcategories to return only those that have
 * at least one POI in the building.
 */
export function filterCategoriesAndSubcategoriesWithExistingPOIs(
  poiCategories: PoiCategories | null,
  pois: Pois,
  keepTopLevels: boolean = true,
): PoiCategories | null {
  if (!poiCategories) return null;
  return poiCategories.filterByIds(
    [
      ...new Set(
        pois?.toArray().flatMap((p) => [p.categoryId, ...p.categoryIds]),
      ),
    ],
    keepTopLevels,
  );
}

/*
 ** Filters and sorts POI categories based on the info from custom field "poiCategoryOrder"
 ** E.g. if poiCategoryOrder = "C1, C3, C2" and I have 4 categories with codes C1, C2, C3, C4
 ** output will be C1, C3, C2
 */
export function filterAndSortPoiCategories(
  poiCategories: PoiCategories,
  poiCategoryOrder: number[],
  currentBuilding: Building,
  debug: boolean,
): PoiCategory[] {
  const poisCustomfield =
    currentBuilding
      ?.getCustomFieldValue("poiCategoryOrder")
      ?.split(",")
      .map((s) => s.trim())
      .map((code) => {
        return poiCategories.findCategoryByCode(code);
      })
      .filter((category): category is PoiCategory => category != null)
      .map((x) => x.id) ?? null;

  const poiCategoryOrderCompat =
    poiCategoryOrder?.length > 0 ? poiCategoryOrder : poisCustomfield;

  if (!poiCategoryOrderCompat || poiCategoryOrderCompat.length == 0) {
    if (debug || poiCategories.length() == 1) return poiCategories.toArray();

    return poiCategories
      .toArray()
      .filter((poiCategory) => poiCategory.id !== NO_POI_CATEGORY);
  }

  const filteredAndOrderedCategories = poiCategoryOrderCompat
    .map((identifier) => {
      return poiCategories.findCategoryById(identifier);
    })
    .filter((category): category is PoiCategory => category != null);
  const poiCategoriesToReturn = new PoiCategories([
    ...new Set(filteredAndOrderedCategories),
  ]).toArray();

  if (debug || poiCategories.length() == 1) return poiCategoriesToReturn;

  return poiCategoriesToReturn.filter(
    (poiCategory) => poiCategory.id !== NO_POI_CATEGORY,
  );
}

export { NO_POI_CATEGORY };
